import type { EventDeleteResultDto } from '@lib/dto/event.dto'
import type { VenuePurgeCounts } from '@lib/dto/venue-purge.dto'

/**
 * What to say after an event's client accounts were asked to go with it.
 *
 * Returns null when nothing was asked of them, and says so plainly when the
 * answer was "nothing happened": a client who is a contact on another event is
 * kept, so choosing to remove accounts does not guarantee any were removed, and
 * silence there would read as if they had been.
 */
export function accountsRemovedLabel(result: EventDeleteResultDto): string | null {
  const { clientsDeleted, clientsDeactivated, clientsKept } = result
  const parts: string[] = []
  if (clientsDeleted > 0) parts.push(`${clientsDeleted} deleted`)
  if (clientsDeactivated > 0) parts.push(`${clientsDeactivated} deactivated`)
  if (parts.length > 0) {
    const kept = clientsKept > 0 ? `; ${clientsKept} kept for other events` : ''
    return `Client accounts: ${parts.join(', ')}${kept}.`
  }
  if (clientsKept > 0) {
    return clientsKept === 1
      ? 'The client account was kept — they are a contact on another event.'
      : `All ${clientsKept} client accounts were kept — they are contacts on other events.`
  }
  return null
}

/**
 * What to say after a venue's calendar was cleared.
 *
 * Says the client half out loud rather than summarizing it, for the same reason
 * the single-event toast does: an account someone can no longer sign into is worth
 * naming. "Kept" has to be said too — anyone still booked at another venue
 * survives whichever choice was made, so asking for the accounts to go does not
 * guarantee that any did.
 */
export function venuePurgeSummary(counts: VenuePurgeCounts): string {
  const { events, clientsDeleted, clientsDeactivated, clientsKept } = counts
  if (events === 0 && clientsDeleted + clientsDeactivated + clientsKept === 0) {
    return 'There was nothing to clear.'
  }
  const head = `Deleted ${events} event${events === 1 ? '' : 's'}.`
  const went: string[] = []
  if (clientsDeleted > 0) went.push(`${clientsDeleted} deleted`)
  if (clientsDeactivated > 0) went.push(`${clientsDeactivated} deactivated`)
  const kept = clientsKept > 0 ? `${clientsKept} kept for other venues` : ''
  if (went.length === 0 && !kept) return head
  const accounts = went.length > 0 ? [went.join(', '), kept].filter(Boolean).join('; ') : kept
  return `${head} Client accounts: ${accounts}.`
}
